import { CodeDividers, WidgetType } from 'common/types/code'
import { DiagramBaseNode, LocalNodesPinned, PinnedNode, PinnedNodeGroup } from 'common/types/model'

export const getNodesFromLocalStorage = (
  appId: string,
  companyId: number,
  appName: string
): {
  existNodeForCurrent?: LocalNodesPinned
  existNodeForCurrentIndex: number
  nodes: LocalNodesPinned[]
} => {
  const nodesFromLocalStorage = localStorage.getItem('nodesPinned')
  if (nodesFromLocalStorage) {
    const nodes: LocalNodesPinned[] = JSON.parse(nodesFromLocalStorage)
    let existNodeForCurrent = nodes.find(
      (node) => node.appId === appId && node.companyId === companyId && node.appName === appName
    )
    let existNodeForCurrentIndex = nodes.findIndex(
      (node) => node.appId === appId && node.companyId === companyId && node.appName === appName
    )
    return { existNodeForCurrent, existNodeForCurrentIndex, nodes }
  }
  return { existNodeForCurrent: undefined, existNodeForCurrentIndex: -1, nodes: [] }
}
export type PinnedData = Partial<Pick<LocalNodesPinned, 'nodes' | 'groups' | 'selectedGroupId'>>

// Merges the given pieces into the app entry, leaving the untouched ones as they are.
// The entry is dropped once it holds neither nodes nor groups
export const savePinnedDataToLocalStorage = (
  appId: string,
  companyId: number,
  appName: string,
  data: PinnedData
): void => {
  const { existNodeForCurrent, existNodeForCurrentIndex, nodes } = getNodesFromLocalStorage(
    appId,
    companyId,
    appName
  )
  const newNodes = [...nodes]
  const pinnedNodes = data.nodes ?? existNodeForCurrent?.nodes ?? []
  const groups = data.groups ?? existNodeForCurrent?.groups ?? []
  const selectedGroupId =
    'selectedGroupId' in data ? data.selectedGroupId : existNodeForCurrent?.selectedGroupId
  const localNodesPinned: LocalNodesPinned = {
    appId,
    companyId,
    appName,
    nodes: pinnedNodes,
    groups,
    selectedGroupId: groups.some((group) => group.id === selectedGroupId)
      ? selectedGroupId
      : undefined
  }
  const isEmpty = pinnedNodes.length === 0 && groups.length === 0
  if (existNodeForCurrentIndex !== -1) {
    if (isEmpty) {
      newNodes.splice(existNodeForCurrentIndex, 1)
    } else {
      newNodes[existNodeForCurrentIndex] = localNodesPinned
    }
  } else if (!isEmpty) {
    newNodes.push(localNodesPinned)
  }
  localStorage.setItem('nodesPinned', JSON.stringify(newNodes))
}

export const savePinnedNodesToLocalStorage = (
  appId: string,
  companyId: number,
  appName: string,
  pinnedNodes: PinnedNode[]
): void => savePinnedDataToLocalStorage(appId, companyId, appName, { nodes: pinnedNodes })

export const savePinnedGroupsToLocalStorage = (
  appId: string,
  companyId: number,
  appName: string,
  groups: PinnedNodeGroup[],
  nodes?: PinnedNode[]
): void => savePinnedDataToLocalStorage(appId, companyId, appName, { groups, nodes })

export const saveSelectedPinnedGroupToLocalStorage = (
  appId: string,
  companyId: number,
  appName: string,
  selectedGroupId?: string
): void => savePinnedDataToLocalStorage(appId, companyId, appName, { selectedGroupId })

export const toPinnedNode = (node: DiagramBaseNode, moduleId: string): PinnedNode => ({
  identifier: node.id,
  label: node.data.title || node.id,
  moduleId,
  nodeType: node.type || 'generic',
  originalId: node.data.nodeClass === 'alias' ? node.data.originalId ?? '' : undefined
})

// Appends the nodes that are not pinned yet, assigning them the selected group. A node
// already pinned elsewhere moves to the selected group, so pinning it again from a group
// tab is never a no-op. Returns whether anything changed
export const pinNodes = (
  appId: string,
  companyId: number,
  appName: string,
  nodesToPin: PinnedNode[]
): boolean => {
  const { existNodeForCurrent } = getNodesFromLocalStorage(appId, companyId, appName)
  const currentNodes = existNodeForCurrent?.nodes ?? []
  const groupId = existNodeForCurrent?.groups?.some(
    (group) => group.id === existNodeForCurrent?.selectedGroupId
  )
    ? existNodeForCurrent?.selectedGroupId
    : undefined
  // Pinning from the "all" tab keeps whichever group the node already had
  const movedNodes = currentNodes.map((node) =>
    groupId &&
    node.groupId !== groupId &&
    nodesToPin.some((nodeToPin) => nodeToPin.identifier === node.identifier)
      ? { ...node, groupId }
      : node
  )
  const newNodes = nodesToPin
    .filter((nodeToPin) => !currentNodes.some((node) => node.identifier === nodeToPin.identifier))
    .map((nodeToPin) => (groupId ? { ...nodeToPin, groupId } : nodeToPin))
  const hasMovedNodes = movedNodes.some((node, index) => node !== currentNodes[index])
  if (newNodes.length === 0 && !hasMovedNodes) return false
  savePinnedNodesToLocalStorage(appId, companyId, appName, [...movedNodes, ...newNodes])
  return true
}

export type PaneNode = {
  type: 'pane'
  key: string
  splitPane?: { split: 'horizontal' | 'vertical' }
  dividerKey?: CodeDividers
  defaultDividerSize?: number
  children?: Array<PaneNode | WidgetNode>
}

type WidgetNode = {
  type: 'widget'
  key: string
  contentType: WidgetType
  dividerKey?: CodeDividers
  defaultDividerSize?: number
}
export const buildDiagramAndDocs = (): PaneNode => {
  return {
    type: 'pane',
    key: 'root',
    splitPane: { split: 'vertical' },
    children: [
      {
        type: 'pane',
        key: 'pane-left',
        splitPane: { split: 'horizontal' },
        dividerKey: CodeDividers.LEFT_PANE_DIVIDER,
        defaultDividerSize: 50,
        children: [
          {
            type: 'widget',
            key: 'w-diagram',
            contentType: WidgetType.DIAGRAM,
            dividerKey: CodeDividers.DIAGRAM_DIVIDER,
            defaultDividerSize: 100
          }
        ]
      },
      {
        type: 'pane',
        key: 'pane-right',
        splitPane: { split: 'horizontal' },
        dividerKey: CodeDividers.RIGHT_PANE_DIVIDER,
        defaultDividerSize: 50,
        children: [
          {
            type: 'widget',
            key: 'w-docs',
            contentType: WidgetType.DOCS,
            dividerKey: CodeDividers.DOCS_DIVIDER,
            defaultDividerSize: 100
          }
        ]
      }
    ]
  }
}
export const buildDiagramAndResult = (isPreview: boolean): PaneNode => {
  return {
    type: 'pane',
    key: 'root',
    splitPane: { split: 'vertical' },
    children: [
      {
        type: 'pane',
        key: 'pane-left',
        splitPane: { split: 'horizontal' },
        dividerKey: CodeDividers.LEFT_PANE_DIVIDER,
        defaultDividerSize: 50,
        children: [
          {
            type: 'widget',
            key: 'w-diagram',
            contentType: WidgetType.DIAGRAM,
            dividerKey: CodeDividers.DIAGRAM_DIVIDER,
            defaultDividerSize: 100
          }
        ]
      },
      {
        type: 'pane',
        key: 'pane-right',
        splitPane: { split: 'horizontal' },
        dividerKey: CodeDividers.RIGHT_PANE_DIVIDER,
        defaultDividerSize: 50,
        children: [
          {
            type: 'widget',
            key: 'w-result-or-preview',
            contentType: isPreview ? WidgetType.PREVIEW : WidgetType.RESULT,
            dividerKey: isPreview ? CodeDividers.PREVIEW_DIVIDER : CodeDividers.RESULT_DIVIDER,
            defaultDividerSize: 50
          },
          {
            type: 'widget',
            key: 'w-docs',
            contentType: WidgetType.DOCS,
            dividerKey: CodeDividers.DOCS_DIVIDER,
            defaultDividerSize: 50
          }
        ]
      }
    ]
  }
}
export const buildDiagramAndCode = (isPreview: boolean): PaneNode => {
  return {
    type: 'pane',
    key: 'root',
    splitPane: { split: 'vertical' },
    children: [
      {
        type: 'pane',
        key: 'pane-left',
        splitPane: { split: 'horizontal' },
        dividerKey: CodeDividers.LEFT_PANE_DIVIDER,
        defaultDividerSize: 50,
        children: [
          {
            type: 'widget',
            key: 'w-diagram',
            contentType: WidgetType.DIAGRAM,
            dividerKey: CodeDividers.DIAGRAM_DIVIDER,
            defaultDividerSize: 100
          }
        ]
      },
      {
        type: 'pane',
        key: 'pane-right',
        splitPane: { split: 'horizontal' },
        dividerKey: CodeDividers.RIGHT_PANE_DIVIDER,
        defaultDividerSize: 50,
        children: [
          {
            type: 'widget',
            key: 'w-code',
            contentType: WidgetType.CODE,
            dividerKey: CodeDividers.CODE_DIVIDER,
            defaultDividerSize: 50
          },
          {
            type: 'widget',
            key: 'w-result-or-preview',
            contentType: isPreview ? WidgetType.PREVIEW : WidgetType.RESULT,
            dividerKey: isPreview ? CodeDividers.PREVIEW_DIVIDER : CodeDividers.RESULT_DIVIDER,
            defaultDividerSize: 50
          }
        ]
      }
    ]
  }
}
